/**
 * CampaignBuilder - Multi-step wizard for creating/editing campaigns
 *
 * Steps:
 * 1. Info - Name, description, dates
 * 2. Trigger - Trigger type selection
 * 3. Audience - AudienceBuilder component
 * 4. Steps - CampaignStepEditor list with drag-and-drop
 * 5. Review - Summary and activation button
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Input,
  Label,
  Textarea,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from '@respectly/ui';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Check,
  Info,
  Target,
  Users,
  ListOrdered,
  Eye,
  Loader2,
  Play,
  Calendar,
  Mail,
  MessageSquare,
  Clock,
} from 'lucide-react';
import { useCampaignsStore } from '../../stores/useCampaignsStore';
import {
  CampaignTriggerBadge,
  CampaignStepEditor,
  AudienceBuilder,
} from '../../components/campaigns';
import type { CampaignTrigger } from '@respectly/shared-types';

// ============================================
// Form Schema
// ============================================

const campaignStepFormSchema = z.object({
  id: z.string(),
  type: z.enum(['email', 'sms', 'wait']),
  order: z.number(),
  delay: z.number().nullable(),
  emailSubject: z.string().nullable().optional(),
  emailContent: z.string().nullable().optional(),
  smsContent: z.string().nullable().optional(),
});

const campaignFormSchema = z.object({
  name: z.string().min(1, 'Campaign name is required'),
  description: z.string().optional(),
  trigger: z.enum(['signup', 'trial_end', 'plan_change', 'inactivity', 'manual']),
  audienceType: z.enum(['all', 'plans', 'tags', 'custom']),
  audiencePlans: z.array(z.string()).nullable(),
  audienceTags: z.array(z.string()).nullable(),
  steps: z.array(campaignStepFormSchema).min(1, 'At least one step is required'),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
});

type CampaignFormData = z.infer<typeof campaignFormSchema>;

// ============================================
// Wizard Step Config
// ============================================

const WIZARD_STEPS = [
  { id: 'info', label: 'Campaign Info', icon: Info },
  { id: 'trigger', label: 'Trigger', icon: Target },
  { id: 'audience', label: 'Audience', icon: Users },
  { id: 'steps', label: 'Steps', icon: ListOrdered },
  { id: 'review', label: 'Review', icon: Eye },
] as const;

type WizardStepId = (typeof WIZARD_STEPS)[number]['id'];

// ============================================
// Trigger Options
// ============================================

const triggerOptions: { value: CampaignTrigger; label: string; description: string }[] = [
  {
    value: 'signup',
    label: 'New Signup',
    description: 'Triggered when a new account signs up',
  },
  {
    value: 'trial_end',
    label: 'Trial Ending',
    description: 'Triggered X days before trial expires',
  },
  {
    value: 'plan_change',
    label: 'Plan Change',
    description: 'Triggered when account changes their plan',
  },
  {
    value: 'inactivity',
    label: 'Inactivity',
    description: 'Triggered after X days of inactivity',
  },
  {
    value: 'manual',
    label: 'Manual Enrollment',
    description: 'Manually enroll accounts into campaign',
  },
];

// ============================================
// Component
// ============================================

export function CampaignBuilder() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const campaignId = (params as { id?: string }).id;
  const isEditMode = !!campaignId;

  const [currentStep, setCurrentStep] = useState<WizardStepId>('info');

  const {
    activeCampaign,
    isLoadingCampaign,
    isPerformingAction,
    loadCampaign,
    createCampaign,
    updateCampaign,
    activateCampaign,
    clearActiveCampaign,
  } = useCampaignsStore();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors },
  } = useForm<CampaignFormData>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      name: '',
      description: '',
      trigger: 'signup',
      audienceType: 'all',
      audiencePlans: null,
      audienceTags: null,
      steps: [
        { id: crypto.randomUUID(), type: 'email', order: 0, delay: null, emailSubject: null, emailContent: null, smsContent: null },
      ],
      startDate: null,
      endDate: null,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'steps',
  });

  const watchedValues = watch();

  // Load campaign in edit mode
  useEffect(() => {
    if (isEditMode && campaignId) {
      loadCampaign(campaignId);
    }
    return () => clearActiveCampaign();
  }, [isEditMode, campaignId, loadCampaign, clearActiveCampaign]);

  // Populate form when campaign loads
  useEffect(() => {
    if (activeCampaign && isEditMode) {
      reset({
        name: activeCampaign.name,
        description: activeCampaign.description || '',
        trigger: activeCampaign.trigger,
        audienceType: activeCampaign.audienceType,
        audiencePlans: activeCampaign.audiencePlans,
        audienceTags: activeCampaign.audienceTags,
        steps: activeCampaign.steps.map((step) => ({
          id: step.id,
          type: step.type,
          order: step.order,
          delay: step.delay ?? null,
          emailSubject: step.emailSubject ?? null,
          emailContent: step.emailContent ?? null,
          smsContent: step.smsContent ?? null,
        })),
        startDate: activeCampaign.startDate
          ? new Date(activeCampaign.startDate).toISOString().split('T')[0]
          : null,
        endDate: activeCampaign.endDate
          ? new Date(activeCampaign.endDate).toISOString().split('T')[0]
          : null,
      });
    }
  }, [activeCampaign, isEditMode, reset]);

  const currentStepIndex = WIZARD_STEPS.findIndex((s) => s.id === currentStep);

  const handleBack = () => {
    if (currentStepIndex > 0) {
      const prevStep = WIZARD_STEPS[currentStepIndex - 1];
      if (prevStep) setCurrentStep(prevStep.id);
    } else {
      navigate({ to: '/campaigns' });
    }
  };

  const handleNext = async () => {
    // Validate current step
    let isValid = true;
    if (currentStep === 'info') {
      isValid = await trigger(['name', 'description']);
    } else if (currentStep === 'trigger') {
      isValid = await trigger(['trigger']);
    } else if (currentStep === 'audience') {
      isValid = await trigger(['audienceType', 'audiencePlans', 'audienceTags']);
    } else if (currentStep === 'steps') {
      isValid = await trigger(['steps']);
    }

    if (isValid && currentStepIndex < WIZARD_STEPS.length - 1) {
      const nextStep = WIZARD_STEPS[currentStepIndex + 1];
      if (nextStep) setCurrentStep(nextStep.id);
    }
  };

  const handleSaveDraft = async () => {
    const data = watchedValues;
    const payload = {
      ...data,
      startDate: data.startDate ? new Date(data.startDate) : undefined,
      endDate: data.endDate ? new Date(data.endDate) : undefined,
      audiencePlans: data.audiencePlans || undefined,
      audienceTags: data.audienceTags || undefined,
    };

    if (isEditMode && campaignId) {
      await updateCampaign(campaignId, payload);
    } else {
      const newId = await createCampaign(payload);
      if (newId) {
        navigate({ to: '/campaigns/$id/edit', params: { id: newId } });
      }
    }
  };

  const onSubmit = async (data: CampaignFormData) => {
    const payload = {
      ...data,
      startDate: data.startDate ? new Date(data.startDate) : undefined,
      endDate: data.endDate ? new Date(data.endDate) : undefined,
      audiencePlans: data.audiencePlans || undefined,
      audienceTags: data.audienceTags || undefined,
    };

    let success = false;
    if (isEditMode && campaignId) {
      success = await updateCampaign(campaignId, payload);
    } else {
      const newId = await createCampaign(payload);
      success = !!newId;
    }

    if (success) {
      navigate({ to: '/campaigns' });
    }
  };

  const handleActivateAndLaunch = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    const data = watchedValues;
    const payload = {
      ...data,
      startDate: data.startDate ? new Date(data.startDate) : undefined,
      endDate: data.endDate ? new Date(data.endDate) : undefined,
      audiencePlans: data.audiencePlans || undefined,
      audienceTags: data.audienceTags || undefined,
    };

    let id: string | null | undefined = campaignId;
    if (isEditMode && campaignId) {
      await updateCampaign(campaignId, payload);
    } else {
      id = await createCampaign(payload);
    }

    if (id) {
      await activateCampaign(id);
      navigate({ to: '/campaigns/$id', params: { id } });
    }
  };

  const handleAddStep = () => {
    append({
      id: crypto.randomUUID(),
      type: 'email',
      order: fields.length,
      delay: null,
      emailSubject: null,
      emailContent: null,
      smsContent: null,
    });
  };

  // Loading state
  if (isEditMode && isLoadingCampaign) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col h-full"
    >
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/campaigns' })}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Campaigns
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                {isEditMode ? 'Edit Campaign' : 'Create Campaign'}
              </h1>
              <p className="text-sm text-slate-500">
                {watchedValues.name || 'New campaign'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSaveDraft} disabled={isPerformingAction}>
              {isPerformingAction ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Draft
            </Button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-4 lg:px-6 pb-4">
          <div className="flex items-center gap-2">
            {WIZARD_STEPS.map((step, index) => {
              const isActive = step.id === currentStep;
              const isCompleted = index < currentStepIndex;
              const StepIcon = step.icon;

              return (
                <div key={step.id} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (index <= currentStepIndex) {
                        setCurrentStep(step.id);
                      }
                    }}
                    disabled={index > currentStepIndex}
                    className={cn(
                      'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                      isActive &&
                        'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                      isCompleted &&
                        'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
                      !isActive &&
                        !isCompleted &&
                        'text-slate-400 dark:text-slate-600',
                      index <= currentStepIndex && 'cursor-pointer hover:opacity-80'
                    )}
                  >
                    <span
                      className={cn(
                        'flex items-center justify-center h-6 w-6 rounded-full text-xs',
                        isActive && 'bg-blue-600 text-white',
                        isCompleted && 'bg-emerald-500 text-white',
                        !isActive && !isCompleted && 'bg-slate-200 dark:bg-slate-700'
                      )}
                    >
                      {isCompleted ? (
                        <Check className="h-3.5 w-3.5" />
                      ) : (
                        <StepIcon className="h-3.5 w-3.5" />
                      )}
                    </span>
                    <span className="hidden sm:inline">{step.label}</span>
                  </button>
                  {index < WIZARD_STEPS.length - 1 && (
                    <div
                      className={cn(
                        'w-8 h-0.5 mx-1',
                        index < currentStepIndex
                          ? 'bg-emerald-500'
                          : 'bg-slate-200 dark:bg-slate-700'
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        <div className="max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Campaign Info */}
            {currentStep === 'info' && (
              <motion.div
                key="info"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Info className="h-5 w-5" />
                      Campaign Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Campaign Name *</Label>
                      <Input
                        id="name"
                        placeholder="e.g., Welcome Series"
                        {...register('name')}
                        className={errors.name ? 'border-red-500' : ''}
                      />
                      {errors.name && (
                        <p className="text-sm text-red-500">{errors.name.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Describe the purpose of this campaign..."
                        rows={3}
                        {...register('description')}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="startDate">
                          <Calendar className="h-4 w-4 inline mr-1" />
                          Start Date
                        </Label>
                        <Input
                          id="startDate"
                          type="date"
                          {...register('startDate')}
                        />
                        <p className="text-xs text-slate-500">
                          Leave blank to start immediately
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="endDate">
                          <Calendar className="h-4 w-4 inline mr-1" />
                          End Date
                        </Label>
                        <Input
                          id="endDate"
                          type="date"
                          {...register('endDate')}
                        />
                        <p className="text-xs text-slate-500">
                          Leave blank to run indefinitely
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 2: Trigger */}
            {currentStep === 'trigger' && (
              <motion.div
                key="trigger"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5" />
                      Campaign Trigger
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-500 mb-4">
                      Select when accounts should be enrolled in this campaign.
                    </p>
                    <div className="space-y-2">
                      {triggerOptions.map((option) => (
                        <label
                          key={option.value}
                          className={cn(
                            'flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
                            watchedValues.trigger === option.value
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                          )}
                        >
                          <input
                            type="radio"
                            value={option.value}
                            {...register('trigger')}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900 dark:text-white">
                                {option.label}
                              </span>
                              <CampaignTriggerBadge trigger={option.value} size="sm" />
                            </div>
                            <p className="text-sm text-slate-500 mt-1">
                              {option.description}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 3: Audience */}
            {currentStep === 'audience' && (
              <motion.div
                key="audience"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      Target Audience
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <AudienceBuilder
                      audienceType={watchedValues.audienceType}
                      audiencePlans={watchedValues.audiencePlans ?? []}
                      audienceTags={watchedValues.audienceTags ?? []}
                      onChange={(type, plans, tags) => {
                        setValue('audienceType', type);
                        setValue('audiencePlans', plans && plans.length > 0 ? plans : null);
                        setValue('audienceTags', tags && tags.length > 0 ? tags : null);
                      }}
                    />
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 4: Campaign Steps */}
            {currentStep === 'steps' && (
              <motion.div
                key="steps"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ListOrdered className="h-5 w-5" />
                      Campaign Steps
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-500 mb-4">
                      Build your campaign sequence. Each account will progress through
                      these steps in order.
                    </p>

                    <div className="space-y-3">
                      {fields.map((field, index) => (
                        <CampaignStepEditor
                          key={field.id}
                          step={{
                            id: field.id,
                            type: watchedValues.steps?.[index]?.type ?? 'email',
                            order: index,
                            delay: watchedValues.steps?.[index]?.delay ?? null,
                            emailSubject: watchedValues.steps?.[index]?.emailSubject ?? null,
                            emailContent: watchedValues.steps?.[index]?.emailContent ?? null,
                            smsContent: watchedValues.steps?.[index]?.smsContent ?? null,
                          }}
                          index={index}
                          onChange={(updatedStep) => {
                            if (updatedStep.type) setValue(`steps.${index}.type`, updatedStep.type);
                            if (updatedStep.delay !== undefined) setValue(`steps.${index}.delay`, updatedStep.delay ?? null);
                            if (updatedStep.emailSubject !== undefined) setValue(`steps.${index}.emailSubject`, updatedStep.emailSubject ?? null);
                            if (updatedStep.emailContent !== undefined) setValue(`steps.${index}.emailContent`, updatedStep.emailContent ?? null);
                            if (updatedStep.smsContent !== undefined) setValue(`steps.${index}.smsContent`, updatedStep.smsContent ?? null);
                          }}
                          onDelete={() => remove(index)}
                        />
                      ))}
                    </div>

                    {errors.steps && (
                      <p className="text-sm text-red-500 mt-2">
                        {errors.steps.message || 'Please add at least one step'}
                      </p>
                    )}

                    <div className="mt-4 flex gap-2">
                      <Button variant="outline" onClick={handleAddStep}>
                        <Mail className="h-4 w-4 mr-2" />
                        Add Email
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() =>
                          append({
                            id: crypto.randomUUID(),
                            type: 'sms',
                            order: fields.length,
                            delay: null,
                            emailSubject: null,
                            emailContent: null,
                            smsContent: null,
                          })
                        }
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Add SMS
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() =>
                          append({
                            id: crypto.randomUUID(),
                            type: 'wait',
                            order: fields.length,
                            delay: 1,
                            emailSubject: null,
                            emailContent: null,
                            smsContent: null,
                          })
                        }
                      >
                        <Clock className="h-4 w-4 mr-2" />
                        Add Wait
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 5: Review */}
            {currentStep === 'review' && (
              <motion.div
                key="review"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Eye className="h-5 w-5" />
                      Review Campaign
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Summary */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label className="text-slate-500">Campaign Name</Label>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {watchedValues.name || '-'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Trigger</Label>
                        <div className="mt-1">
                          <CampaignTriggerBadge trigger={watchedValues.trigger} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-500">Audience</Label>
                        <p className="font-medium text-slate-900 dark:text-white capitalize">
                          {watchedValues.audienceType === 'all'
                            ? 'All accounts'
                            : watchedValues.audienceType === 'plans'
                            ? `${watchedValues.audiencePlans?.length || 0} plan(s)`
                            : watchedValues.audienceType === 'tags'
                            ? `${watchedValues.audienceTags?.length || 0} tag(s)`
                            : 'Custom'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Steps</Label>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {watchedValues.steps?.length || 0} step(s)
                        </p>
                      </div>
                      {watchedValues.startDate && (
                        <div>
                          <Label className="text-slate-500">Start Date</Label>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {new Date(watchedValues.startDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {watchedValues.endDate && (
                        <div>
                          <Label className="text-slate-500">End Date</Label>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {new Date(watchedValues.endDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>

                    {watchedValues.description && (
                      <div>
                        <Label className="text-slate-500">Description</Label>
                        <p className="text-slate-700 dark:text-slate-300 mt-1">
                          {watchedValues.description}
                        </p>
                      </div>
                    )}

                    {/* Steps Preview */}
                    <div>
                      <Label className="text-slate-500 mb-2 block">Campaign Flow</Label>
                      <div className="flex flex-wrap items-center gap-2">
                        {watchedValues.steps?.map((step, index) => (
                          <div key={step.id} className="flex items-center gap-2">
                            <div
                              className={cn(
                                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
                                step.type === 'email' &&
                                  'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
                                step.type === 'sms' &&
                                  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
                                step.type === 'wait' &&
                                  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                              )}
                            >
                              {step.type === 'email' && <Mail className="h-3.5 w-3.5" />}
                              {step.type === 'sms' && <MessageSquare className="h-3.5 w-3.5" />}
                              {step.type === 'wait' && <Clock className="h-3.5 w-3.5" />}
                              {step.type === 'wait'
                                ? `Wait ${step.delay} day(s)`
                                : step.type.toUpperCase()}
                            </div>
                            {index < (watchedValues.steps?.length || 0) - 1 && (
                              <ArrowRight className="h-4 w-4 text-slate-400" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Actions */}
                <Card className="bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
                  <CardContent className="py-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                          Ready to launch?
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          Save as draft or activate the campaign now.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={handleSubmit(onSubmit)}
                          disabled={isPerformingAction}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Save as Draft
                        </Button>
                        <Button
                          onClick={handleActivateAndLaunch}
                          disabled={isPerformingAction}
                        >
                          {isPerformingAction ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4 mr-2" />
                          )}
                          Activate Campaign
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
        <div className="max-w-3xl mx-auto flex justify-between">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {currentStepIndex === 0 ? 'Cancel' : 'Back'}
          </Button>

          {currentStep !== 'review' && (
            <Button onClick={handleNext}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
